// src/components/modals/AddUserModal.tsx
import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Modal } from '@/components/common/Modal';
import { Button } from '@/components/common/Button';
import { Input } from '@/components/common/Input';
import { Select } from '@/components/common/Select';
import { useUserStore } from '@/store/userStore';
import { USER_ROLES, USER_STATUS } from '@/utils/constants';

const userSchema = z.object({
  name: z.string().min(1, 'User name is required'),
  email: z.string().email('Invalid email address'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
  role: z.enum([USER_ROLES.ADMIN, USER_ROLES.TEAM_MEMBER]),
});

type UserFormData = z.infer<typeof userSchema>;

interface AddUserModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const AddUserModal: React.FC<AddUserModalProps> = ({ isOpen, onClose }) => {
  const { createUser } = useUserStore();
  
  const { register, handleSubmit, formState: { errors, isSubmitting }, reset } = useForm<UserFormData>({
    resolver: zodResolver(userSchema),
    defaultValues: {
      role: USER_ROLES.TEAM_MEMBER
    }
  });

  const onSubmit = async (data: UserFormData) => {
    try {
      await createUser({
        ...data,
        status: USER_STATUS.INVITED
      });
      reset();
      onClose();
    } catch (error) {
      // Error is handled in the store
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add New User">
      <div className="mb-4 p-3 bg-blue-50 rounded-lg px-3">
        <p className="text-sm text-blue-800">
          Invite a new team member by entering their details below. 
          We will send them a welcome email with a link to accept the invitation.
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 px-3">
        <Input
          label="User Name"
          placeholder="John Doe"
          {...register('name')}
          error={errors.name?.message}
          required
        />

        <Input
          label="Email Address"
          type="email"
          placeholder="john@example.com"
          {...register('email')}
          error={errors.email?.message}
          required
        />

        <Input
          label="Password"
          type="password"
          placeholder="••••••••"
          {...register('password')}
          error={errors.password?.message}
          required
        />
       <p className="text-xs text-gray-500">
          Minimum 8 characters. Use a mix of letters, numbers and symbols.
        </p>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Role
          </label>
          <Select {...register('role')} error={errors.role?.message}>
            <option value={USER_ROLES.TEAM_MEMBER}>Team Member</option>
            <option value={USER_ROLES.ADMIN}>Admin</option>
          </Select>
        </div>

        <div className="flex justify-end space-x-3 pt-4">
          <Button variant="secondary" type="button" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={isSubmitting} loading={isSubmitting}>
            Add User
          </Button>
        </div>
      </form>
    </Modal>
  );
};